import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaStethoscope, FaHospital, FaArrowLeft, FaChevronLeft, FaChevronRight, FaRobot, FaHeart, FaRegHeart, FaComment, FaPaperPlane, FaEdit, FaTrashAlt, FaTimes, FaCheck } from 'react-icons/fa';
import Skeleton from '../components/common/Skeleton';
import Logo from '../components/common/Logo';
import { getSearchResults } from '../api/result';
import { addFavorite, removeFavoriteByDetails, checkFavorite } from '../api/favorite';
import { addComment, getComments, updateComment, deleteComment } from '../api/comment';

/**
 * SearchInfo Component
 * 검색 조건 표시
 */
const SearchInfo = ({ clinicName, hospitalName, locationName }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
      <FaStethoscope className="text-primary" />
      검색 조건
    </h2>
    <div className="flex flex-wrap gap-3">
      {clinicName && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg font-medium">
          <FaStethoscope className="text-sm" />
          <span>{clinicName}</span>
        </div>
      )}
      {hospitalName && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium">
          <FaHospital className="text-sm" />
          <span>{hospitalName}</span>
        </div>
      )}
      {locationName && (
        <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg font-medium">
          <FaMapMarkerAlt className="text-sm" />
          <span>{locationName}</span>
        </div>
      )}
    </div>
  </div>
);

/**
 * AIComment Component
 * AI 추천 코멘트
 */
const AIComment = ({ comment }) => (
  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-100 p-6 mb-6">
    <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
      <FaRobot className="text-blue-600" />
      AI 추천 분석
    </h2>
    <p className="text-gray-700 leading-relaxed whitespace-pre-line">{comment}</p>
  </div>
);

/**
 * StarRating Component
 * 별점 선택/표시
 */
const StarRating = ({ value, onChange, readonly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`text-lg transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <span className={(hover || value) >= star ? 'text-yellow-400' : 'text-gray-300'}>★</span>
        </button>
      ))}
    </div>
  );
};

/**
 * CommentSection Component
 * 병원별 댓글 섹션
 */
const CommentSection = ({ hospitalName, clinicCode }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [newScore, setNewScore] = useState(5);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editComment, setEditComment] = useState('');
  const [editScore, setEditScore] = useState(5);
  const isLoggedIn = !!localStorage.getItem('token');

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getComments(hospitalName, clinicCode);
      setComments(data);
    } catch (err) {
      console.error('댓글 불러오기 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [hospitalName, clinicCode]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      setSubmitting(true);
      const created = await addComment({ hospitalName, clinicCode, comment: newComment.trim(), score: newScore });
      setComments(prev => [created, ...prev]);
      setNewComment('');
      setNewScore(5);
    } catch (err) {
      alert('댓글 작성에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editComment.trim()) return;
    try {
      const updated = await updateComment(id, editComment.trim(), editScore);
      setComments(prev => prev.map(c => c.id === id ? updated : c));
      setEditingId(null);
      setEditComment('');
      setEditScore(5);
    } catch (err) {
      alert('댓글 수정에 실패했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await deleteComment(id);
      setComments(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      alert('댓글 삭제에 실패했습니다.');
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditComment(c.comment);
    setEditScore(c.score);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditComment('');
    setEditScore(5);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      {/* 댓글 입력 */}
      {isLoggedIn ? (
        <form onSubmit={handleSubmit} className="mb-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 font-medium">평점</span>
            <StarRating value={newScore} onChange={setNewScore} />
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="댓글을 입력하세요..."
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all"
              maxLength={500}
            />
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1"
            >
              <FaPaperPlane className="text-xs" />
              작성
            </button>
          </div>
        </form>
      ) : (
        <p className="text-sm text-gray-400 mb-4 text-center py-2 bg-gray-50 rounded-lg">
          로그인 후 댓글을 작성할 수 있습니다.
        </p>
      )}

      {/* 댓글 목록 */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-2">댓글 로딩 중...</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-2">아직 댓글이 없습니다.</p>
      ) : (
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-3 group">
              {/* 아바타 */}
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">{c.userName ? c.userName.charAt(0) : '익'}</span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-gray-800">{c.userName || '익명'}</span>
                  <StarRating value={c.score} readonly />
                  <span className="text-xs text-gray-400">{formatDate(c.createdAt)}</span>
                  {c.updatedAt && c.updatedAt !== c.createdAt && (
                    <span className="text-xs text-gray-300">(수정됨)</span>
                  )}
                </div>

                {editingId === c.id ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">평점</span>
                      <StarRating value={editScore} onChange={setEditScore} />
                    </div>
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={editComment}
                        onChange={(e) => setEditComment(e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                        autoFocus
                      />
                      <button onClick={() => handleUpdate(c.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                        <FaCheck className="text-sm" />
                      </button>
                      <button onClick={cancelEdit} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors">
                        <FaTimes className="text-sm" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 break-words">{c.comment}</p>
                )}
              </div>

              {/* 수정/삭제 버튼 — userName이 있으면 본인 댓글 */}
              {c.userName && editingId !== c.id && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(c)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all">
                    <FaEdit className="text-xs" />
                  </button>
                  <button onClick={() => handleDelete(c.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                    <FaTrashAlt className="text-xs" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * ResultCard Component
 * 개별 검색 결과 카드
 */
const ResultCard = ({ result, defaultClinicCode }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const targetClinicCode = result.clinicCode || defaultClinicCode;

  // 초기 찜 상태 확인
  useEffect(() => {
    const checkStatus = async () => {
      // 로그인이 안되어 있으면 API 호출 안함 (토큰 체크)
      const token = localStorage.getItem('token');
      if (!token) return;

      console.log(`Checking favorite for ${result.hospitalName}, code: ${targetClinicCode}`);
      try {
        const favorited = await checkFavorite(result.hospitalName, targetClinicCode);
        console.log(`Favorite status for ${result.hospitalName}: ${favorited}`);
        setIsFavorite(favorited);
      } catch (error) {
        console.error("Failed to check favorite status", error);
      }
    };
    if (result.hospitalName && targetClinicCode) {
      checkStatus();
    }
  }, [result.hospitalName, targetClinicCode]);

  const priceRange = result.minPrice === result.maxPrice
    ? `${result.minPrice.toLocaleString()}원`
    : `${result.minPrice.toLocaleString()}원 ~ ${result.maxPrice.toLocaleString()}원`;

  const toggleFavorite = async (e) => {
    e.stopPropagation(); // 카드 클릭 이벤트 전파 방지

    // 로그인 체크
    const token = localStorage.getItem('token');
    if (!token) {
      alert("로그인이 필요한 기능입니다.");
      return;
    }

    // 낙관적 업데이트
    const prevStatus = isFavorite;
    setIsFavorite(!prevStatus);

    try {
      if (!prevStatus) {
        // 찜하기 추가
        await addFavorite({
          hospitalName: result.hospitalName,
          clinicName: result.clinicName,
          clinicCode: targetClinicCode, // result에 clinicCode가 없으면 default 사용
          location: result.location,
          minPrice: result.minPrice,
          maxPrice: result.maxPrice
        });
      } else {
        // 찜하기 삭제
        await removeFavoriteByDetails(result.hospitalName, targetClinicCode);
      }
    } catch (error) {
      console.error("찜하기 변경 실패", error);
      // 실패 시 롤백
      setIsFavorite(prevStatus);
      alert("요청 처리에 실패했습니다.");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow relative">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold text-gray-900">{result.hospitalName}</h3>
            <button
              onClick={toggleFavorite}
              className="p-1 rounded-full hover:bg-red-50 transition-colors"
              aria-label={isFavorite ? "찜하기 취소" : "찜하기"}
            >
              {isFavorite ? (
                <FaHeart className="text-red-500 text-lg animate-scale-in" />
              ) : (
                <FaRegHeart className="text-gray-300 text-lg hover:text-red-400 transition-colors" />
              )}
            </button>
          </div>
          <p className="text-sm text-gray-600 flex items-center gap-1 mb-2">
            <FaMapMarkerAlt className="text-gray-400" />
            {result.location}
          </p>
          <p className="text-sm text-gray-500">{result.clinicName}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-primary">{priceRange}</div>
        </div>
      </div>

      {/* 댓글 토글 버튼 */}
      <button
        onClick={() => setShowComments(!showComments)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors mt-2"
      >
        <FaComment className="text-xs" />
        {showComments ? '댓글 접기' : '💬 댓글 보기'}
      </button>

      {/* 댓글 섹션 */}
      {showComments && (
        <CommentSection hospitalName={result.hospitalName} clinicCode={targetClinicCode} />
      )}
    </div>
  );
};

/**
 * ResultsLoading Component
 * 로딩 스피너
 */
const ResultsLoading = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
    <div className="relative">
      {/* 외곽 원 */}
      <div className="w-20 h-20 border-4 border-gray-200 rounded-full"></div>
      {/* 회전하는 원 */}
      <div className="absolute top-0 left-0 w-20 h-20 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
    </div>
    <div className="text-center space-y-2">
      <p className="text-lg font-semibold text-gray-900">검색 중입니다</p>
      <p className="text-sm text-gray-500">최적의 병원 정보를 찾고 있어요</p>
    </div>
  </div>
);

/**
 * Pagination Component
 * 페이지네이션 UI
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`p-2 rounded-lg border ${currentPage === 1
          ? 'border-gray-200 text-gray-400 cursor-not-allowed'
          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        aria-label="이전 페이지"
      >
        <FaChevronLeft />
      </button>

      {getPageNumbers().map((page, index) => (
        page === '...' ? (
          <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`min-w-[40px] px-3 py-2 rounded-lg border font-medium transition-colors ${currentPage === page
              ? 'bg-primary text-white border-primary'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            {page}
          </button>
        )
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`p-2 rounded-lg border ${currentPage === totalPages
          ? 'border-gray-200 text-gray-400 cursor-not-allowed'
          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        aria-label="다음 페이지"
      >
        <FaChevronRight />
      </button>
    </div >
  );
};

/**
 * ResultsContent Component
 * 실제 검색 결과 내용
 */
const ResultsContent = ({ clinicCode, clinicName, hospitalName, locationName, sidoCode, sigguCode }) => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [aiComment, setAiComment] = useState('');
  const [resultCount, setResultCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('price-low');
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 10;

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        if (!clinicCode) {
          navigate('/');
          return;
        }

        const data = await getSearchResults({
          clinicCode,
          hospitalName: hospitalName || null,
          sidoCode: sidoCode || null,
          sigguCode: sigguCode || null
        });

        setResults(data.list || []);
        setAiComment(data.aiComment || '');
        setResultCount(data.resultCount || 0);

      } catch (err) {
        setError('검색 결과를 불러오는데 실패했습니다.');
        console.error('검색 실패:', err);
        // 세션이 없거나 오류 시 홈으로 리다이렉트
        setTimeout(() => navigate('/'), 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [clinicCode, hospitalName, sidoCode, sigguCode, navigate]);

  // 검색 결과가 없을 때 홈으로 자동 이동
  useEffect(() => {
    if (!loading && results.length === 0 && !error) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, results.length, error, navigate]);

  // 정렬된 결과 계산
  const sortedResults = useMemo(() => {
    const sorted = [...results].sort((a, b) => {
      if (sortBy === 'price-low') {
        return a.minPrice - b.minPrice;
      } else if (sortBy === 'price-high') {
        return b.maxPrice - a.maxPrice;
      }
      return 0;
    });
    return sorted;
  }, [results, sortBy]);

  // 페이지네이션 계산
  const totalPages = Math.ceil(sortedResults.length / resultsPerPage);
  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const currentResults = sortedResults.slice(indexOfFirstResult, indexOfLastResult);

  // 페이지 변경 핸들러
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 정렬 변경 핸들러
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1); // 정렬 변경 시 첫 페이지로
  };

  if (loading) {
    return <ResultsLoading />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90"
        >
          홈으로 이동
        </button>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg mb-2">검색 결과가 없습니다.</p>
        <p className="text-gray-400 text-sm mb-4">잠시 후 홈으로 이동합니다...</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90"
        >
          홈으로 이동
        </button>
      </div>
    );
  }

  return (
    <div>
      <SearchInfo clinicName={clinicName} hospitalName={hospitalName} locationName={locationName} />

      {aiComment && <AIComment comment={aiComment} />}

      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          검색 결과 <span className="text-primary">{resultCount}</span>건
        </h2>
        <select
          value={sortBy}
          onChange={handleSortChange}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="price-low">가격 낮은순</option>
          <option value="price-high">가격 높은순</option>
        </select>
      </div>

      <div className="space-y-4">
        {currentResults.map((result) => (
          <ResultCard key={result.id} result={result} defaultClinicCode={clinicCode} />
        ))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

/**
 * ResultsPage Component
 * 검색 결과 페이지
 */
const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // state에서 검색 데이터 가져오기 (없으면 세션 스토리지 확인)
  const [searchData, setSearchData] = useState(() => {
    const state = location.state;
    const stored = sessionStorage.getItem('lastSearchData');
    return state || (stored ? JSON.parse(stored) : {});
  });

  useEffect(() => {
    if (location.state) {
      setSearchData(location.state);
      sessionStorage.setItem('lastSearchData', JSON.stringify(location.state));
    }
  }, [location.state]);

  const {
    clinicCode,
    clinicName,
    hospitalName,
    locationName,
    sidoCode,
    sigguCode
  } = searchData;

  // state가 없어도 (새로고침 시) 세션으로 조회하므로 리다이렉트하지 않음
  // 리다이렉트는 API 에러 시 ResultsContent에서 처리

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm bg-white/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="뒤로가기"
            >
              <FaArrowLeft className="text-gray-600" />
            </button>
            <Logo />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ResultsContent
          clinicCode={clinicCode}
          clinicName={clinicName}
          hospitalName={hospitalName}
          locationName={locationName}
          sidoCode={sidoCode}
          sigguCode={sigguCode}
        />
      </div>
    </main>
  );
};

export default ResultsPage;